import { useCallback, useEffect, useState } from 'react';

import * as service from 'services/availabilitySlotsService';
import { MeetingRequestDTO } from 'services/availabilitySlotsService';
import { AvailabilitySlot, CreateAvailabilitySlotDTO } from 'types/availabilitySlotTypes';

export function useAvailabilitySlots(userId: string | undefined) {
  const [slots, setSlots] = useState<AvailabilitySlot[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchSlots = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    setError(null);
    try {
      const data = await service.getUserSlots(userId);
      setSlots(data);
    } catch (err: any) {
      setError(err.message || 'Erro ao buscar horários');
    } finally {
      setLoading(false);
    }
  }, [userId]);

  const addSlot = useCallback(
    async (slot: CreateAvailabilitySlotDTO) => {
      if (!userId) return;
      setLoading(true);
      setError(null);
      try {
        const newSlot = await service.createSlot(slot);
        setSlots(prev => [...prev, newSlot]);
      } catch (err: any) {
        setError(err.message || 'Erro ao adicionar horário');
      } finally {
        setLoading(false);
      }
    },
    [userId]
  );

  const removeSlot = useCallback(
    async (slotId: string) => {
      if (!userId) return;
      setLoading(true);
      setError(null);
      try {
        await service.deleteSlot(slotId, userId);
        setSlots(prev => prev.filter(s => s.id !== slotId));
      } catch (err: any) {
        setError(err.message || 'Erro ao remover horário');
      } finally {
        setLoading(false);
      }
    },
    [userId]
  );

  // Busca slots disponíveis para UM dia (dateStr: YYYY-MM-DD)
  const fetchAvailableSlotsForWeek = useCallback(
    async (dateStr: string) => {
      if (!userId) return {};
      const data = await service.getAvailableSlotsForDate(userId, dateStr);
      // Retorna no formato { [dateStr]: data }
      return { [dateStr]: data };
    },
    [userId]
  );

  const requestMeeting = useCallback(async (meetingData: MeetingRequestDTO) => {
    return await service.createMeetingRequest(meetingData);
  }, []);

  useEffect(() => {
    fetchSlots();
  }, [fetchSlots]);

  return {
    slots,
    loading,
    error,
    fetchSlots,
    addSlot,
    removeSlot,
    fetchAvailableSlotsForWeek,
    requestMeeting,
  };
}
